import { CoreEvent, eventNames } from '@carvana/core-events';
import { Clickable } from '@carvana/merch-components';
import { useEffectOnce } from '@carvana/merch-react-hooks';
import { useState } from 'react';
import TrackWrapper from '@/common/TrackWrapper';
import formatEvent from '@/home/utils/formatEvent';
import { FavoriteVehicle } from '~/contracts/common';
import { RecommendedVehicle } from '~/contracts/vdp';
import { SuggestedVehicle } from '~/services/home/contracts/SuggestedVehiclesData';
import LinkCard, { LinkType } from './LinkCard';
import useGetVDPLinksToShow, { VDPLinkTitles } from './useGetVDPLinksToShow';
import {
  Header,
  HeaderWrapper,
  LinkCardContainer,
  LinksContainer,
  linkStyles
} from '../LinkCard.styles';
import LinksLoadingCard, { LinkCardType } from '../LinksLoadingCard';

function getLinkCardType(header: VDPLinkTitles): LinkType {
  switch (header) {
    case VDPLinkTitles.Favorite:
      return LinkType.Favorite;
    case VDPLinkTitles.Recent:
      return LinkType.Recent;
    case VDPLinkTitles.Suggested:
      return LinkType.Suggested;
    default:
      return LinkType.Suggested;
  }
}

export default function VDPLinks() {
  const {
    header,
    vehicles,
    showLink = false,
    showLoading
  } = useGetVDPLinksToShow();
  const [vehiclesToShow, setVehiclesToShow] = useState<
    FavoriteVehicle[] | RecommendedVehicle[] | SuggestedVehicle[]
  >([]);
  const [hasLink, setHasLink] = useState(false);
  const [headerToShow, setHeaderToShow] = useState(VDPLinkTitles.None);
  const [isLoaded, setIsLoaded] = useState(false);

  //So that we don't swap vehicles when a user favorites a vehicle
  useEffectOnce(
    () => {
      setVehiclesToShow(vehicles);
      setHasLink(showLink);
      setHeaderToShow(header);
      setIsLoaded(true);
    },
    [header, showLink, vehicles],
    !showLoading
  );

  return isLoaded ? (
    <TrackWrapper
      event={formatEvent('Viewed - Home feed card', {
        description: 'VDP card',
        type: getLinkCardType(headerToShow),
        item: 'vehicle'
      })}
      trackOnce
    >
      <LinkCardContainer data-qa="vdp-link-card">
        <HeaderWrapper>
          <Header data-qa="vdp-link-card-header">{headerToShow}</Header>
          {hasLink && (
            <Clickable
              data-analytics-id="Home feed card"
              data-analytics-attributes={JSON.stringify({
                description: 'VDP card',
                type: 'favorites',
                item: 'view all'
              })}
              className={linkStyles}
              onClick={() => {
                // @ts-ignore
                CoreEvent.emit(eventNames.favorites.drawer.open);
              }}
              data-qa="vdp-fav-view-all"
            >
              View all
            </Clickable>
          )}
        </HeaderWrapper>
        <LinksContainer data-qa="vdp-link-vehicles">
          {vehiclesToShow.map(vehicle => (
            <LinkCard
              vehicle={{ ...vehicle, price: vehicle.price.total }}
              key={vehicle.vehicleId}
              type={getLinkCardType(headerToShow)}
            />
          ))}
        </LinksContainer>
      </LinkCardContainer>
    </TrackWrapper>
  ) : (
    <LinksLoadingCard card={LinkCardType.VDP} />
  );
}
